import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { apiClient } from "@/lib/api";
import SEO from "@/components/SEO";
import { getSEO } from "@/lib/seo-config";
import { ShieldCheck, FileText, Scale, ChevronRight } from "lucide-react";

const LEGAL_PAGES = [
  { to: "/mentions-legales", label: "Mentions légales", icon: ShieldCheck, tag: "// 01" },
  { to: "/cgu", label: "CGU", icon: FileText, tag: "// 02" },
  { to: "/cgv", label: "CGV", icon: Scale, tag: "// 03" },
];

function PageTabs() {
  const { pathname } = useLocation();
  return (
    <div className="border-2 border-[#0A0A0B] bg-white flex flex-col sm:flex-row sticky top-[73px] z-30" data-testid="legal-tabs">
      {LEGAL_PAGES.map((p, idx) => {
        const active = pathname === p.to;
        const Icon = p.icon;
        return (
          <Link
            key={p.to}
            to={p.to}
            data-testid={`legal-tab-${p.to.replace("/", "")}`}
            className={`flex-1 px-5 py-4 flex items-center gap-3 transition border-[#0A0A0B] ${
              idx < LEGAL_PAGES.length - 1 ? "sm:border-r-2 border-b-2 sm:border-b-0" : ""
            } ${
              active
                ? "bg-[#0A0A0B] text-white"
                : "bg-white text-[#0A0A0B] hover:bg-[#FF4D00] hover:text-white"
            }`}
          >
            <Icon className="w-4 h-4" strokeWidth={2.5} />
            <div>
              <div className={`mono text-[10px] uppercase tracking-widest ${active ? "text-[#FF4D00]" : "text-zinc-500"}`}>{p.tag}</div>
              <div className="text-sm font-semibold">{p.label}</div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

export function LegalLayout({ overline, title, summary, children, lastUpdate = "Février 2026", seoPath }) {
  return (
    <div className="min-h-screen bg-[#F7F7F9] flex flex-col">
      {seoPath && <SEO cfg={getSEO(seoPath)} />}
      <Navbar />

      {/* Breadcrumb */}
      <div className="border-b-2 border-[#0A0A0B] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-3 mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2" data-testid="legal-breadcrumb">
          <Link to="/" className="hover:text-[#FF4D00]">Accueil</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#0A0A0B]">{title}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#0A0A0B] text-white py-16 border-b-2 border-[#0A0A0B]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">{overline}</div>
          <h1 className="display text-5xl lg:text-6xl tracking-tighter mt-3">{title}</h1>
          {summary && <p className="mt-4 max-w-2xl text-zinc-300 text-base">{summary}</p>}
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-6">Dernière mise à jour : {lastUpdate}</div>
        </div>
      </section>

      {/* Tabs + Content */}
      <div className="max-w-7xl mx-auto px-6 py-10 flex-1 w-full">
        <PageTabs />
        <article
          data-testid="legal-content"
          className="mt-8 border-2 border-[#0A0A0B] bg-white p-6 sm:p-10 lg:p-14 max-w-none text-[15px] leading-relaxed text-zinc-700
            [&_h2]:display [&_h2]:text-2xl [&_h2]:tracking-tight [&_h2]:text-[#0A0A0B] [&_h2]:font-bold
            [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:pl-4 [&_h2]:border-l-4 [&_h2]:border-[#FF4D00]
            [&_h2:first-child]:mt-0
            [&_p]:my-4
            [&_a]:text-[#FF4D00] [&_a]:font-medium hover:[&_a]:underline
            [&_strong]:text-[#0A0A0B] [&_strong]:font-semibold
            [&_ul]:my-4 [&_ul]:pl-5 [&_ul]:list-disc [&_li]:my-1.5
            [&_code]:bg-zinc-100 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[13px] [&_code]:rounded-none [&_code]:border [&_code]:border-zinc-200"
        >
          {children}
        </article>
        <div className="mt-6 mono text-[10px] uppercase tracking-widest text-zinc-500">
          Document opposable · SAS BATISUR CONNECT · 29 rue Tronchet, 75008 Paris
        </div>
      </div>

      <Footer />
    </div>
  );
}

export function useCompany() {
  const [c, setC] = useState({});
  useEffect(() => {
    apiClient.get("/settings/public").then(({ data }) => setC(data.settings || {})).catch(() => {});
  }, []);
  return c;
}

// ── Mentions légales ─────────────────────────────────────────────────────────
export function MentionsLegales() {
  const c = useCompany();
  const addr = [c.address_line1, c.address_line2, [c.postal_code, c.city].filter(Boolean).join(" "), c.country].filter(Boolean).join(", ");
  return (
    <LegalLayout
      overline="// 01 — Mentions légales"
      title="Mentions légales"
      summary="Informations légales de l'éditeur du site, conformément à l'article 6 de la loi LCEN n° 2004-575 du 21 juin 2004."
      seoPath="/mentions-legales"
    >
      <h2>Éditeur du site</h2>
      <p>
        Le site <strong>{c.company_name || "Batisur Connect"}</strong> (ci-après « Batisur ») est édité par :
      </p>
      <ul>
        <li><strong>Raison sociale</strong> : {c.company_name || "BATISUR CONNECT"}</li>
        <li><strong>Forme juridique</strong> : {c.legal_form || "SAS"}</li>
        {c.capital && <li><strong>Capital social</strong> : {c.capital}</li>}
        <li><strong>Siège social</strong> : {addr || "29 rue Tronchet, 75008 Paris, France"}</li>
        {c.siret && <li><strong>SIRET</strong> : {c.siret}</li>}
        {c.vat_number && <li><strong>N° TVA intracommunautaire</strong> : {c.vat_number}</li>}
        {c.contact_email && <li><strong>Email</strong> : <a href={`mailto:${c.contact_email}`}>{c.contact_email}</a></li>}
        {c.contact_phone && <li><strong>Téléphone</strong> : {c.contact_phone}</li>}
      </ul>

      <h2>Directeur de la publication</h2>
      <p>Le représentant légal de {c.company_name || "BATISUR CONNECT"}.</p>

      <h2>Hébergement</h2>
      <p>
        Le site est hébergé en France par <strong>OVH SAS</strong> (dénomination commerciale OVHcloud),
        société au capital de 10 174 560 €, immatriculée au RCS de Lille sous le n° 424 761 419,
        dont le siège social est situé <strong>2 rue Kellermann, 59100 Roubaix, France</strong>.
      </p>
      <ul>
        <li><strong>Téléphone</strong> : +33 9 72 10 10 07</li>
        <li><strong>Site web</strong> : <a href="https://www.ovhcloud.com" target="_blank" rel="noopener noreferrer">www.ovhcloud.com</a></li>
        <li><strong>Localisation des serveurs</strong> : datacenters OVHcloud situés en France (Union européenne) — conformité RGPD et souveraineté des données</li>
      </ul>
      <p>
        La base de données applicative (MongoDB) est également hébergée sur l'infrastructure OVHcloud en France.
      </p>

      <h2>Propriété intellectuelle</h2>
      <p>
        L'ensemble du contenu du site (textes, graphismes, logos, photographies, code source) est la propriété exclusive de {c.company_name || "BATISUR CONNECT"} ou de ses partenaires. Toute reproduction non autorisée est interdite (articles L.122-4 et L.335-2 du Code de la propriété intellectuelle).
      </p>

      <h2>Protection des données personnelles (RGPD)</h2>
      <p>
        Conformément au Règlement (UE) 2016/679 (RGPD) et à la loi « Informatique et Libertés » modifiée :
      </p>
      <ul>
        <li><strong>Responsable du traitement</strong> : {c.company_name || "BATISUR CONNECT"}</li>
        <li><strong>Finalités</strong> : fourniture du service, mise en relation, séquestre des paiements, génération de factures et certificats, lutte contre la fraude</li>
        <li><strong>Base légale</strong> : exécution du contrat, obligations légales (comptables, fiscales), intérêt légitime</li>
        <li><strong>Durée de conservation</strong> : durée du contrat + 10 ans pour les pièces comptables</li>
        <li><strong>Vos droits</strong> : accès, rectification, effacement, portabilité, opposition. Adressez votre demande à <a href={`mailto:${c.contact_email || "contact@batisur-connect.com"}`}>{c.contact_email || "contact@batisur-connect.com"}</a></li>
        <li><strong>Réclamation</strong> : auprès de la CNIL (<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>)</li>
      </ul>

      <h2>Cookies</h2>
      <p>
        Le site utilise uniquement des cookies techniques strictement nécessaires à l'authentification (JWT) et au bon fonctionnement du service. Aucun cookie publicitaire ou de tracking tiers n'est déposé.
      </p>

      <h2>Médiation de la consommation</h2>
      <p>
        Conformément à l'article L.616-1 du Code de la consommation, en cas de litige non résolu, le consommateur peut recourir gratuitement au médiateur de la consommation compétent.
      </p>
    </LegalLayout>
  );
}

// ── CGU ──────────────────────────────────────────────────────────────────────
export function CGU() {
  const c = useCompany();
  return (
    <LegalLayout
      overline="// 02 — Conditions générales d'utilisation"
      title="CGU"
      summary="Règles d'accès et d'usage de la plateforme Batisur pour les demandeurs et les artisans du bâtiment."
      seoPath="/cgu"
    >
      <h2>1. Objet</h2>
      <p>
        Les présentes Conditions Générales d'Utilisation (« CGU ») régissent l'accès et l'utilisation de la plateforme {c.company_name || "Batisur Connect"} (ci-après « la Plateforme »), accessible à l'adresse <strong>www.batisur-connect.com</strong>. La Plateforme met en relation des demandeurs (particuliers ou professionnels) avec des artisans du bâtiment, et sécurise les paiements via un mécanisme de séquestre (escrow).
      </p>

      <h2>2. Acceptation</h2>
      <p>
        L'inscription et l'utilisation de la Plateforme valent acceptation pleine et entière des CGU. Toute personne refusant les CGU doit cesser immédiatement d'utiliser la Plateforme.
      </p>

      <h2>3. Inscription</h2>
      <p>
        L'utilisateur doit fournir des informations exactes, complètes et à jour. Il est seul responsable de la confidentialité de son mot de passe et de toute activité réalisée depuis son compte.
      </p>
      <p>
        Pour les artisans : la vérification du SIRET via la base officielle Sirene/INSEE est fortement recommandée et obligatoire pour les devis ≥ 5 000 € TTC. Pour le Pack Confiance Or, le SIRET vérifié et 10 missions terminées sont requis.
      </p>

      <h2>4. Fonctionnement des flux financiers et des validations</h2>
      <p>
        Le Demandeur, après acceptation d'un devis, procède au paiement du montant des travaux via l'outil sécurisé <strong>Stripe Checkout</strong>. Les fonds sont encaissés par Stripe et immédiatement affectés au compte <strong>Stripe Express</strong> de l'Artisan.
      </p>
      <p>
        Conformément au fonctionnement de l'infrastructure <strong>Stripe Connect</strong>, ces fonds sont temporairement rendus indisponibles au virement vers le compte bancaire de l'Artisan (<em>Payout Block</em>). La libération et la disponibilité de ces fonds pour le virement externe sont conditionnées à la <strong>validation finale par le Demandeur</strong> depuis son espace personnel sur la Plateforme, ainsi qu'à la <strong>publication par l'Artisan d'au moins une photo de « Finition »</strong> dans le journal de chantier.
      </p>
      <p>
        {c.company_name || "BATISUR CONNECT"} intervient exclusivement en tant qu'<strong>intermédiaire technique</strong> transmettant à Stripe l'ordre informatique de libération des fonds. {c.company_name || "BATISUR CONNECT"} <strong>ne détient, ne conserve, ni ne manipule à aucun moment</strong> les fonds liés aux transactions.
      </p>
      <p>
        Les avenants (Travaux Supplémentaires) suivent le même mécanisme : proposés par l'Artisan avec photos justificatives, acceptés et payés par le Demandeur via un Stripe Checkout dédié, avec le même verrouillage/libération conditionnels.
      </p>

      <h2>5. Obligations des utilisateurs</h2>
      <ul>
        <li>Ne pas publier de contenu illicite, diffamatoire, injurieux ou portant atteinte aux droits de tiers</li>
        <li>Ne pas tenter de contourner le mécanisme de séquestre par paiement direct hors Plateforme</li>
        <li>Respecter les délais et engagements contractuels (devis acceptés)</li>
        <li>Conserver la confidentialité des données échangées via la messagerie</li>
      </ul>

      <h2>6. Responsabilité de la Plateforme et Service de Médiation</h2>

      <h3>6.1 Rôle d'intermédiaire technique</h3>
      <p>
        {c.company_name || "BATISUR CONNECT"} agit exclusivement en qualité d'<strong>intermédiaire technique et de mise en relation</strong>. La Plateforme n'est pas partie aux contrats (devis, marchés de travaux) conclus directement entre les Demandeurs et les Artisans. En conséquence, {c.company_name || "BATISUR CONNECT"} <strong>ne saurait être tenue responsable</strong> des malfaçons, des retards, des non-conformités, des abandons de chantier ou de tout dommage direct ou indirect lié à l'exécution des travaux.
      </p>

      <h3>6.2 Service de médiation en cas de litige</h3>
      <p>
        En cas de désaccord entre le Demandeur et l'Artisan (par exemple : refus du Demandeur de valider la libération des fonds alors que l'Artisan a publié la photo de Finition), les Utilisateurs s'engagent à en informer la Plateforme <strong>sans délai</strong>.
      </p>
      <p>
        {c.company_name || "BATISUR CONNECT"} propose un <strong>service gratuit de médiation amiable</strong> pour aider les parties à trouver un accord. Dans ce cadre, la Plateforme peut analyser les éléments du journal de chantier (messages, photos justificatives, devis).
      </p>
      <p>
        L'intervention de {c.company_name || "BATISUR CONNECT"} se limite à un rôle de <strong>facilitateur</strong>. Les propositions de la Plateforme <strong>ne revêtent aucun caractère obligatoire ou juridictionnel</strong>. Si la médiation n'aboutit pas dans un délai de <strong>30 jours</strong> à compter de sa saisine, les fonds resteront bloqués sur le compte Stripe Express de l'Artisan jusqu'à l'obtention :
      </p>
      <ul>
        <li>Soit d'un <strong>accord écrit et signé par les deux parties</strong> (Demandeur et Artisan) spécifiant la répartition des fonds ;</li>
        <li>Soit d'une <strong>décision de justice exécutoire</strong> ou d'une <strong>ordonnance de référé</strong> désignant le bénéficiaire des sommes.</li>
      </ul>

      <h2>7. Suspension et résiliation</h2>
      <p>
        {c.company_name || "BATISUR CONNECT"} se réserve le droit de suspendre ou supprimer tout compte en cas de violation des CGU, de fraude, ou de comportement contraire aux bonnes pratiques professionnelles.
      </p>

      <h2>8. Modification</h2>
      <p>Les CGU peuvent être modifiées à tout moment. Les utilisateurs en seront informés par email.</p>

      <h2>9. Droit applicable</h2>
      <p>Les CGU sont régies par le droit français. Tout litige relève de la compétence des tribunaux français.</p>
    </LegalLayout>
  );
}

// ── CGV ──────────────────────────────────────────────────────────────────────
export function CGV() {
  const c = useCompany();
  const rate = c.commission_rate ? `${(c.commission_rate * 100).toFixed(0)}%` : "10 %";
  const vat = c.vat_rate ? `${(c.vat_rate * 100).toFixed(0)}%` : "20 %";
  return (
    <LegalLayout
      overline="// 03 — Conditions générales de vente"
      title="CGV"
      summary="Conditions tarifaires, modalités de paiement et règles de commission appliquées par Batisur Connect."
      seoPath="/cgv"
    >
      <h2>1. Champ d'application</h2>
      <p>
        Les présentes Conditions Générales de Vente régissent les services payants proposés par {c.company_name || "BATISUR CONNECT"} sur la Plateforme, à savoir :
      </p>
      <ul>
        <li>Le service d'intermédiation et de séquestre de paiement (commission)</li>
        <li>Les abonnements Premium et Or pour les artisans</li>
      </ul>

      <h2>2. Commission de la Plateforme</h2>
      <p>
        Une commission est prélevée à la libération des fonds séquestrés, selon des paliers dégressifs configurables :
      </p>
      <ul>
        <li>0 → 5 000 € : {rate} par partie (taux nominal)</li>
        <li>5 000 → 25 000 € : 7 % par partie</li>
        <li>25 000 → 100 000 € : 5 % par partie</li>
        <li>100 000 → 500 000 € : 3 % par partie</li>
        <li>&gt; 500 000 € : 1,5 % par partie</li>
      </ul>
      <p>
        Calcul par palier marginal (chaque euro paie le taux de sa tranche). La commission est répartie de manière équivalente entre le demandeur et l'artisan. Les paliers en vigueur sont accessibles à tout moment via l'endpoint public <code>/api/settings/public</code>.
      </p>

      <h2>3. Abonnements artisans</h2>
      <ul>
        <li><strong>Standard</strong> : gratuit, sans engagement</li>
        <li><strong>Premium</strong> : 29 € / 30 jours TTC — mise en avant + badge vérifié</li>
        <li><strong>Or (Pack Confiance)</strong> : 49 € / 30 jours TTC — réservé aux artisans avec SIRET vérifié + 10 missions terminées</li>
      </ul>
      <p>
        Le paiement est effectué via Stripe Checkout. L'abonnement est valable 30 jours à compter du paiement, sans tacite reconduction. Une facture PDF est émise automatiquement.
      </p>

      <h2>4. TVA</h2>
      <p>
        Toutes les factures émises par {c.company_name || "BATISUR CONNECT"} incluent la TVA française au taux en vigueur ({vat}). Les factures sont conformes aux articles 289 et suivants du Code général des impôts.
      </p>

      <h2>5. Pénalités de retard de chantier</h2>
      <p>
        Le demandeur peut activer, au moment de la création d'une mission, un système de pénalités de retard inspiré du CCAG-Travaux (taux journalier configurable, plafond configurable). En cas de retard avéré, le montant des pénalités est automatiquement déduit du payout artisan et reversé au demandeur. Le demandeur peut renoncer à appliquer la pénalité lors de la validation finale.
      </p>

      <h2>6. Modalités de paiement</h2>
      <p>
        Les paiements s'effectuent exclusivement via Stripe (établissement de paiement agréé). Aucun paiement direct hors Plateforme n'est autorisé. Les coordonnées bancaires de séquestre sont gérées par Stripe et ne sont jamais transmises à {c.company_name || "BATISUR CONNECT"}.
      </p>

      <h2>7. Droit de rétractation</h2>
      <p>
        Conformément à l'article L.221-28 du Code de la consommation, le droit de rétractation ne s'applique pas aux services pleinement exécutés avant la fin du délai (séquestre déjà initié). Pour les abonnements artisans Premium/Or, un délai de rétractation de 14 jours est accordé sauf si l'utilisateur en a fait l'usage avant l'expiration du délai.
      </p>

      <h2>8. Litige et médiation</h2>
      <p>
        En cas de litige entre demandeur et artisan, {c.company_name || "BATISUR CONNECT"} propose une médiation interne via un administrateur de la Plateforme, qui peut consulter le journal de chantier (photos opposables) et trancher avant tout recours juridictionnel.
      </p>

      <h2>9. Garanties</h2>
      <p>
        {c.company_name || "BATISUR CONNECT"} ne fournit aucune garantie sur la qualité ou la conformité des travaux exécutés, qui relèvent de la seule responsabilité de l'artisan. Le certificat de bonne fin de travaux émis par la Plateforme constitue une preuve formelle de la réception sans réserve, opposable notamment dans le cadre de la garantie décennale et lors d'une revente immobilière.
      </p>

      <h2>10. Loi applicable</h2>
      <p>Les présentes CGV sont régies par le droit français. Tout litige est de la compétence exclusive des tribunaux français.</p>
    </LegalLayout>
  );
}
